import { useMemo, useState, type ReactNode } from 'react';
import { Gauge } from '../../components/Gauge';
import { Drawer } from '../../components/data';
import { inBook } from '../../lib/amBook';
import { formatCompactCurrency, formatDate, formatNumber } from '../../lib/format';
import type { AmClientRow, AmSummary } from '../../lib/types';

/* The bars the dials are held to. Account management had none until these
   were agreed on 2026-10-25; they live here rather than in settings until
   somebody needs to change one without a release. */
const TARGETS = {
  coverage: 90,
  revenueCovered: 90,
  onTime: 90,
  health: 80,
  clearOfFindings: 80,
} as const;

/** The page already flags a client under this health score. */
const LOW_HEALTH = 60;

type Drill = { title: string; subtitle: string; rows: AmClientRow[] };

const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : null);
const initials = (name: string) =>
  name === 'Unassigned'
    ? '–'
    : name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]!.toUpperCase())
        .join('');
const ownerOf = (c: AmClientRow) => (c.AccountManager ?? '').trim() || 'Unassigned';

/**
 * The account-management overview: the four counts that want somebody's name
 * against them, then a row of dials for how the book as a whole is doing.
 *
 * EVERY DIAL AND EVERY TILE OPENS THE CLIENTS BEHIND IT. The figures are
 * worked out here from the same client list the drawer shows, so "14 of 215"
 * on a dial is fourteen rows when you click it — not a second query that
 * might disagree with the first.
 */
export function AmOverview({
  summary: s,
  clients,
  onOpenClient,
  onOpenPipeline,
}: {
  summary: AmSummary;
  clients: AmClientRow[];
  onOpenClient: (id: string) => void;
  onOpenPipeline: () => void;
}) {
  const [drill, setDrill] = useState<Drill | null>(null);

  const f = useMemo(() => {
    const booked = clients.filter((c) => c.HasReview);
    const unbooked = clients.filter((c) => !c.HasReview);
    /* Prospects and churned clients carry no revenue the business has (lib/amBook.ts). */
    const mrr = (rows: AmClientRow[]) => rows.reduce((sum, c) => sum + (inBook(c.Status) ? (c.MonthlyRevenue ?? 0) : 0), 0);
    /* On time is asked of the reviews still ahead of somebody, not of every
       client: a client with nothing booked is the coverage dial's problem. */
    const open = clients.filter((c) => c.NextReviewDate !== null);
    const overdue = open.filter((c) => c.ReviewOverdue);
    const audited = clients.filter((c) => c.Audits > 0);
    const withFindings = audited.filter((c) => c.HighRisks > 0);
    const lowHealth = clients.filter((c) => c.HealthScore !== null && c.HealthScore < LOW_HEALTH);
    const atRisk = clients.filter((c) => c.Status === 'At Risk');
    return {
      booked,
      unbooked,
      totalMrr: mrr(clients),
      bookedMrr: mrr(booked),
      open,
      overdue,
      audited,
      withFindings,
      lowHealth,
      atRisk,
    };
  }, [clients]);

  const byMrr = (rows: AmClientRow[]) =>
    [...rows].sort((a, b) => (b.MonthlyRevenue ?? 0) - (a.MonthlyRevenue ?? 0));
  const show = (title: string, subtitle: string, rows: AmClientRow[]) =>
    setDrill({ title, subtitle, rows });

  const coverage = pct(f.booked.length, clients.length);
  const revenueCovered = pct(f.bookedMrr, f.totalMrr);
  const onTime = pct(f.open.length - f.overdue.length, f.open.length);
  const clear = pct(f.audited.length - f.withFindings.length, f.audited.length);

  return (
    /* am-coverage-card: printing a document from a tab below hides this —
       it is page furniture, not part of any scope or audit. */
    <div className="am-overview am-coverage-card mb-4">
      {/* One band on a wide screen — the cards, the rings and the managers
          side by side — so the whole dashboard is about as tall as one of
          them. Stacked below that. */}
      <div className="am-overview-split">
      <div className="am-kpis">
        {/* The book itself, as the one dark card: navy is structure in this
            palette, and the rest of the row reads against it. */}
        <div className="am-kpi feature">
          <span className="am-kpi-icon" aria-hidden="true">☺</span>
          <div className="am-kpi-label">Active clients</div>
          <div className="am-kpi-figure">{formatNumber(s.Clients, 'number')}</div>
          <div className="am-kpi-foot">
            <strong>{formatNumber(s.TotalMrr, 'currency')}</strong> a month recurring
          </div>
        </div>
        {/* AT RISK before the pipeline: what might leave is a larger number
            than what might arrive. Flagged whenever it is not zero. */}
        <HeroTile
          icon="!"
          label="Clients at risk"
          figure={s.AtRiskClients}
          flagged={s.AtRiskClients > 0}
          foot={
            s.AtRiskClients > 0
              ? `${formatNumber(s.AtRiskMrr, 'currency')} a month at risk`
              : 'none carrying the status'
          }
          onClick={
            f.atRisk.length > 0
              ? () => show('Clients at risk', 'Carrying the "At Risk" status, biggest first.', byMrr(f.atRisk))
              : undefined
          }
        />
        <HeroTile
          icon="◷"
          label="Reviews overdue"
          figure={s.ReviewsOverdue}
          flagged={s.ReviewsOverdue > 0}
          foot={s.ReviewsDueSoon > 0 ? `${s.ReviewsDueSoon} more due in 30 days` : 'none due in 30 days'}
          onClick={
            f.overdue.length > 0
              ? () =>
                  show(
                    'Overdue reviews',
                    'Clients whose next review date has passed, longest overdue first.',
                    [...f.overdue].sort((a, b) => (a.NextReviewDate ?? '').localeCompare(b.NextReviewDate ?? '')),
                  )
              : undefined
          }
        />
        <HeroTile
          icon="◇"
          label="Open recommendations"
          figure={s.OpenRecommendations}
          foot={
            <>
              {formatCompactCurrency(s.OpenRecommendationMonthly)}/mo ·{' '}
              {s.OpenRecommendationAnnual > 0 && <>{formatCompactCurrency(s.OpenRecommendationAnnual)}/yr · </>}
              {formatCompactCurrency(s.OpenRecommendationOneOff)} one-off ·{' '}
              {formatNumber(s.OpenRecommendationHours, 'number')} h of work
            </>
          }
          onClick={onOpenPipeline}
          drillLabel="Open the pipeline"
        />
      </div>

      <section className="am-panel">
        <header className="am-panel-head">
          <div>
            <h2>Health of the book</h2>
            <p title="Each ring is held to a target, marked by the tick. Click one to see the clients behind it.">
              Each ring is held to a target, marked by the tick. Click one to see the clients behind it.
            </p>
          </div>
        </header>
      <div className="gauge-grid am-gauges">
        <Gauge
          label="Review coverage"
          value={coverage === null ? null : Math.round(coverage)}
          target={TARGETS.coverage}
          hint={`${f.booked.length} of ${clients.length} have a review booked`}
          onClick={() =>
            show(
              'Nothing booked',
              'Clients with no review on record. They never show as overdue, because there is nothing to be overdue — biggest first.',
              byMrr(f.unbooked),
            )
          }
          drillLabel={`${f.unbooked.length} with nothing booked`}
        />
        <Gauge
          label="Revenue under review"
          value={revenueCovered === null ? null : Math.round(revenueCovered)}
          target={TARGETS.revenueCovered}
          hint={`${formatCompactCurrency(f.bookedMrr)} of ${formatCompactCurrency(f.totalMrr)} a month`}
          onClick={() =>
            show(
              'Revenue with no review',
              `${formatNumber(f.totalMrr - f.bookedMrr, 'currency')} a month sits with clients nobody has a review booked with.`,
              byMrr(f.unbooked),
            )
          }
          drillLabel="Where it sits"
        />
        <Gauge
          label="Reviews on time"
          value={onTime === null ? null : Math.round(onTime)}
          target={TARGETS.onTime}
          hint={
            f.open.length === 0
              ? 'no upcoming reviews'
              : `${f.overdue.length} of ${f.open.length} booked clients overdue`
          }
          onClick={
            f.overdue.length > 0
              ? () =>
                  show(
                    'Overdue reviews',
                    'Clients whose next review date has passed, longest overdue first.',
                    [...f.overdue].sort((a, b) => (a.NextReviewDate ?? '').localeCompare(b.NextReviewDate ?? '')),
                  )
              : undefined
          }
          drillLabel="See the overdue"
        />
        <Gauge
          label="Average health"
          value={s.AverageHealth === null ? null : Math.round(s.AverageHealth)}
          target={TARGETS.health}
          unit=""
          hint={`${f.lowHealth.length} ${f.lowHealth.length === 1 ? 'client' : 'clients'} under ${LOW_HEALTH}`}
          onClick={() =>
            show(
              'Weakest health',
              `Every client under the target of ${TARGETS.health}, weakest first.`,
              clients
                .filter((c) => c.HealthScore !== null && c.HealthScore < TARGETS.health)
                .sort((a, b) => (a.HealthScore ?? 0) - (b.HealthScore ?? 0)),
            )
          }
          drillLabel="Weakest first"
        />
        <Gauge
          label="Clear of serious findings"
          value={clear === null ? null : Math.round(clear)}
          target={TARGETS.clearOfFindings}
          hint={
            f.audited.length === 0
              ? 'no client audited yet'
              : `${f.withFindings.length} of ${f.audited.length} audited · ${s.HighRisks} high or critical`
          }
          onClick={
            f.withFindings.length > 0
              ? () =>
                  show(
                    'High or critical findings',
                    'Audited clients with at least one high or critical answer, most first.',
                    [...f.withFindings].sort((a, b) => b.HighRisks - a.HighRisks),
                  )
              : undefined
          }
          drillLabel="See the findings"
        />
      </div>

      </section>

      <CoverageByManager
        clients={clients}
        onSelect={(manager, rows) =>
          show(
            `${manager}: nothing booked`,
            `${rows.length} of ${clients.filter((c) => ownerOf(c) === manager).length} clients with no review on record, biggest first.`,
            byMrr(rows),
          )
        }
      />
      </div>

      <Drawer
        open={drill !== null}
        onClose={() => setDrill(null)}
        title={drill?.title ?? ''}
        subtitle={drill?.subtitle}
        wide
      >
        {drill && <DrillTable rows={drill.rows} onOpen={onOpenClient} />}
      </Drawer>
    </div>
  );
}

function HeroTile({
  icon,
  label,
  figure,
  foot,
  flagged = false,
  onClick,
  drillLabel = 'See the clients',
}: {
  icon: string;
  label: string;
  figure: number;
  foot: ReactNode;
  flagged?: boolean;
  onClick?: () => void;
  drillLabel?: string;
}) {
  const body = (
    <>
      <span className="am-kpi-icon" aria-hidden="true">
        {icon}
      </span>
      <div className="am-kpi-label">{label}</div>
      <div className="am-kpi-figure">{formatNumber(figure, 'number')}</div>
      <div className="am-kpi-foot">{foot}</div>
      {onClick && <div className="am-kpi-drill">{drillLabel} →</div>}
    </>
  );
  const cls = `am-kpi${flagged ? ' flagged' : ''}`;
  return onClick ? (
    <button type="button" className={`${cls} is-link`} onClick={onClick} title={`${drillLabel} →`}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/**
 * Review coverage by account manager.
 *
 * THE BAR IS THE PERCENTAGE, NOT THE BOOK. This used to draw each manager as
 * a column as tall as their client count, which made the biggest book with
 * the worst coverage the biggest, boldest shape on the page — it read as the
 * winner. Now every bar runs on the same 0–100 scale against the same target
 * tick, so the comparison is the one that matters, and the size of the job
 * is said in words at the end of the row.
 *
 * Worst gap first, counted in clients: one client at 0% is not the morning's
 * work, and forty at 7% is. A row opens that manager's unbooked clients.
 */
function CoverageByManager({
  clients,
  onSelect,
}: {
  clients: AmClientRow[];
  onSelect: (manager: string, unbooked: AmClientRow[]) => void;
}) {
  const rows = useMemo(() => {
    const groups = new Map<string, AmClientRow[]>();
    for (const c of clients) {
      const key = ownerOf(c);
      groups.set(key, [...(groups.get(key) ?? []), c]);
    }
    return [...groups.entries()]
      .map(([manager, list]) => {
        const booked = list.filter((c) => c.HasReview).length;
        return {
          manager,
          clients: list.length,
          booked,
          unbooked: list.filter((c) => !c.HasReview),
          pct: (booked / list.length) * 100,
        };
      })
      .sort(
        (a, b) =>
          b.unbooked.length - a.unbooked.length || b.clients - a.clients || a.manager.localeCompare(b.manager),
      );
  }, [clients]);

  /* One manager is not a comparison — the coverage dial already said it. */
  if (rows.length < 2) return null;

  return (
    <section className="am-panel">
      <header className="am-panel-head">
        <div>
          <h2>Review coverage by account manager</h2>
          <p title="Share of each manager's clients with a review booked, worst gap first.">
            Share of each manager's clients with a review booked, worst gap first.
          </p>
        </div>
        <span className="am-legend">
          <span className="am-key booked" /> booked
          <span className="am-mgr-key-tick" /> {TARGETS.coverage}% target
        </span>
      </header>
      <div className="am-mgr" role="list">
          {rows.map((r) => {
            const shown = Math.round(r.pct);
            return (
              <button
                key={r.manager}
                type="button"
                role="listitem"
                className="am-mgr-row"
                disabled={r.unbooked.length === 0}
                onClick={() => onSelect(r.manager, r.unbooked)}
                title={`${r.manager} — ${r.booked} of ${r.clients} booked, ${r.unbooked.length} nothing booked`}
              >
                <span className="am-mgr-avatar" aria-hidden="true">
                  {initials(r.manager)}
                </span>
                <span className="am-mgr-name">
                  <span className="strong">{r.manager}</span>
                  <span className="xs muted">
                    {r.booked} of {r.clients} booked
                  </span>
                </span>
                <span
                  className="am-mgr-track"
                  role="img"
                  aria-label={`${r.manager}: ${shown}% covered against a ${TARGETS.coverage}% target`}
                >
                  {r.booked > 0 && <span className="am-mgr-fill" style={{ width: `${r.pct}%` }} />}
                  <span className="am-mgr-tick" style={{ left: `${TARGETS.coverage}%` }} />
                </span>
                <span className="am-mgr-pct">{shown}%</span>
                <span className={`am-mgr-gap${r.unbooked.length > 0 ? ' short' : ''}`}>
                  {r.unbooked.length > 0 ? `${r.unbooked.length} not booked →` : 'All booked'}
                </span>
              </button>
            );
          })}
      </div>
    </section>
  );
}

function DrillTable({ rows, onOpen }: { rows: AmClientRow[]; onOpen: (id: string) => void }) {
  if (rows.length === 0) return <p className="small muted">No clients.</p>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Client</th>
            <th>Manager</th>
            <th className="num">MRR</th>
            <th className="num">Health</th>
            <th className="num">High risk</th>
            <th>Next review</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.AmClientId}>
              <td>
                <button type="button" className="link-button strong" onClick={() => onOpen(c.AmClientId)}>
                  {c.Name}
                </button>
              </td>
              <td className="xs">{c.AccountManager ?? <span className="subtle">Unassigned</span>}</td>
              <td className="num">{c.MonthlyRevenue ? formatCompactCurrency(c.MonthlyRevenue) : '—'}</td>
              <td className="num">
                <span className={c.HealthScore !== null && c.HealthScore < LOW_HEALTH ? 'accent' : ''}>
                  {c.HealthScore ?? '—'}
                </span>
              </td>
              <td className="num">{c.HighRisks > 0 ? <span className="accent">{c.HighRisks}</span> : '—'}</td>
              <td className="xs mono">
                {c.NextReviewDate === null ? (
                  <span className="subtle">none booked</span>
                ) : c.ReviewOverdue ? (
                  <span className="badge badge-critical">{formatDate(c.NextReviewDate)}</span>
                ) : (
                  formatDate(c.NextReviewDate)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
